'use client';

import React from 'react';
import { PiXLight } from 'react-icons/pi';
import { toast as sonnerToast } from 'sonner';
import Image from 'next/image';
import { getStaticAssetPath } from '@/lib/utils';

const siteLogo = getStaticAssetPath('/static/siteLogo.svg');

interface ToastProps {
  id: string | number;
  title?: string;
  description: string;
  type?: 'error' | 'success' | 'warning' | 'info' | 'custom';
  button?: {
    label: string;
    onClick: () => void;
  };
  duration?: number; // Duration in milliseconds
}

function toast(toast: Omit<ToastProps, 'id'>) {
  return sonnerToast.custom(
    (id) => (
      <Toast
        id={id}
        title={toast.title}
        description={toast.description}
        type={toast.type}
        button={toast.button}
      />
    ),
    {
      duration: toast.duration, // Pass duration to Sonner's options object
    }
  );
}

/** A fully custom toast that still maintains the animations and interactions. */
function Toast(props: ToastProps) {
  const { title, description, id, type = 'custom', button } = props;

  // Determine if we should use vertical layout based on button text length
  const useVerticalLayout = button && button.label && button.label.length > 8;

  // Map toast types to colors
  const typeColors = {
    error: 'bg-destructive',
    success: 'bg-accent-green',
    warning: 'bg-accent-yellow',
    info: 'bg-accent-blue',
    custom: 'bg-accent-gray',
  };

  // Generate conditional ID based on toast type
  const toastId = type === 'success' ? 'popupSuccess' : type === 'error' ? 'popupFailed' : undefined;

  if (useVerticalLayout) {
    return (
      <div
        className='rounded-md bg-white shadow-lg ring-1 ring-black/5 w-[356px] p-5'
        id={toastId}
      >
        <div className='flex items-stretch gap-4 mb-3'>
          {/* Dynamic colored line */}
          <div className={`w-[2px] rounded-full ${typeColors[type]}`}></div>
          <div className='flex-1 flex flex-col gap-3'>
            <h3 className='font-semibold text-sm text-black'>{title}</h3>
            <p className='font-normal text-sm text-black'>{description}</p>
            {/* Button below content */}
        <div className='flex'>
          {button && (
            <button
              className='rounded bg-black px-3 py-1 text-sm font-medium text-white hover:bg-zinc-700'
              onClick={button.onClick}
            >
              {button.label || 'Dismiss'}
            </button>
          )}
        </div>
          </div>
        </div>

        

        {/* Close button */}
        <button
          onClick={() => sonnerToast.dismiss(id)}
          className='absolute p-0.5 top-2 right-2 text-black hover:text-zinc-600 focus:outline-none transition-colors duration-200 ease-in-out'
          aria-label='Close'
        >
          <PiXLight size={16} />
        </button>
      </div>
    );
  }

  // Horizontal layout for short button text
  return (
    <div
      className='flex rounded-md bg-white shadow-lg ring-1 ring-black/5 w-[356px] items-center p-5'
      id={toastId}
    >
      <div className='ml-4 flex flex-1 gap-3 items-center relative' style={{ minWidth: 0 }}
        >
        {/* Dynamic colored line */}
        <div className={`w-[2px] h-[60px] rounded-full block ${typeColors[type]}`}></div>
        <Image
          src={siteLogo}
          alt='Darpiano Logo'
          width={36}
          height={36}
          className='my-1'
          priority={true}
        />
        <div className='flex-1'>
          <h3 className='font-semibold text-sm text-black'>{title}</h3>
          <p className='font-normal text-sm text-black'>{description}</p>
        </div>
      </div>
      <div className='ml-5 shrink-0 rounded-md'>
        {button && (
          <button
            className='rounded bg-black px-3 py-1 text-sm font-medium text-white hover:bg-zinc-700'
            onClick={button.onClick}
          >
            {button.label || 'Dismiss'}
          </button>
        )}
      </div>

      {/* Close button */}
      <button
        onClick={() => sonnerToast.dismiss(id)}
        className='absolute p-0.5 top-2 right-2 text-black hover:text-zinc-600 focus:outline-none transition-colors duration-200 ease-in-out'
        aria-label='Close'
      >
        <PiXLight size={16} />
      </button>
    </div>
  );
}

export default toast;
